<template>
  <SoDialog
    v-model="value"
    :loading="isLoading"
    card-title="Historic Schedules"
  >
    <SoErrorBoundary>
      <template v-if="!isLoading">
        <SoAlert
          v-if="!paymentScheduleItems.length"
          type="info"
          data-testid="historic-schedule-alert"
        >
          There are no historic schedules for this account
        </SoAlert>
        <SoDataTable
          v-else
          :headers="headers"
          :items="paymentScheduleItems"
          :searchable="false"
          :has-pagination="false"
        />
      </template>
    </SoErrorBoundary>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { IPaymentSchedule } from "@/models/paymentSchedule/paymentSchedule.types"
  import {
    SoDataTableColumnType,
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import { yesNo } from "@/helpers/stringHelpers"

  export default defineComponent({
    components: {
      SoDialog,
      SoDataTable,
      SoAlert,
      SoErrorBoundary,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        headers: [
          {
            title: "From",
            key: "from",
            type: SoDataTableColumnType.DATE,
          },
          {
            title: "To",
            key: "to",
            type: SoDataTableColumnType.DATE,
          },
          {
            title: "Duration",
            key: "duration",
            sortable: false,
          },
          {
            title: "Day of Month",
            key: "dayOfMonth",
            sortable: false,
          },
          {
            title: "Seasonal",
            key: "isSeasonal",
            sortable: false,
          },
          {
            title: "Amount",
            key: "amount",
            sortable: false,
          },
          {
            title: "Created",
            key: "created",
            type: SoDataTableColumnType.DATE,
          },
        ] as SoDataTableHeader[],
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      isLoading() {
        return this.current.paymentSchedule.isFetching
      },
      paymentScheduleItems(): SoDataTableItem[] {
        const schedules = this.current.paymentSchedule?.data ?? []
        return schedules.map((schedule: IPaymentSchedule) => ({
          from: schedule.fromDt,
          to: schedule.toDt,
          duration: schedule.duration,
          dayOfMonth: schedule.frequencyAlignmentDt.date(),
          isSeasonal: yesNo(schedule.isSeasonal),
          amount: schedule.amountFormatted,
          created: schedule.createdAt,
        }))
      },
    },
  })
</script>
